import React, { useState, useEffect } from 'react';
import { FileText, Edit, Trash2, Calendar, Search, Download, Eye, Save, X } from 'lucide-react';
import { User } from '../lib/types';
import { getSavedPosts } from '../lib/auth';
import { supabase } from '../lib/supabase';
import TextEditor from './TextEditor';

interface SavedPostsProps {
  user: User;
}

const SavedPosts: React.FC<SavedPostsProps> = ({ user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [savedPosts, setSavedPosts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setIsLoading(true);
        const posts = await getSavedPosts(user.id);
        setSavedPosts(posts);
      } catch (error) {
        console.error('Error loading saved posts:', error);
        setError('Une erreur est survenue lors du chargement de vos offres');
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, [user.id]);

  // Filter posts by search term
  const filteredPosts = savedPosts.filter(post => 
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleStartEdit = (post: any) => {
    setEditingPost(post.id);
    setEditedContent(post.content);
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setEditedContent('');
  };

  const handleSaveEdit = async (postId: string) => {
    try {
      const { error: updateError } = await supabase
        .from('job_posts')
        .update({ content: editedContent })
        .eq('id', postId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Update local state
      setSavedPosts(posts => 
        posts.map(post => 
          post.id === postId 
            ? { ...post, content: editedContent }
            : post
        )
      );

      setEditingPost(null);
      setEditedContent('');
    } catch (error) {
      console.error('Error updating post:', error);
      setError('Une erreur est survenue lors de la mise à jour');
    }
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('job_posts')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      setSavedPosts(posts => posts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      setError('Une erreur est survenue lors de la suppression');
    }
  };

  const handleExport = (post: any) => {
    try {
      const blob = new Blob([post.content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${post.title || 'offre'}-${formatDate(post.created_at)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting post:', error);
      setError('Une erreur est survenue lors de l\'export');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Mes offres d'emploi sauvegardées</h2>
      
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Rechercher une offre..."
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {filteredPosts.length > 0 ? (
        <div className="space-y-4">
          {filteredPosts.map(post => (
            <div key={post.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{post.title || 'Sans titre'}</h3>
                  <p className="text-indigo-600">{post.company || 'Entreprise non spécifiée'}</p>
                </div>
                <div className="flex items-center space-x-2 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{formatDate(post.created_at)}</span>
                </div>
              </div>
              
              {editingPost === post.id ? (
                <div className="mt-4">
                  <TextEditor
                    value={editedContent}
                    onChange={setEditedContent}
                    height="300px"
                  />
                  <div className="flex justify-end space-x-3 mt-4">
                    <button
                      onClick={handleCancelEdit}
                      className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Annuler</span>
                    </button>
                    <button
                      onClick={() => handleSaveEdit(post.id)}
                      className="flex items-center space-x-1 text-green-600 hover:text-green-800 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>Enregistrer</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mt-2 line-clamp-2">
                    {post.content?.substring(0, 200) + '...' || 'Aucun contenu'}
                  </p>
                  
                  <div className="flex justify-end mt-4 space-x-3">
                    <button 
                      onClick={() => {/* Implement view functionality */}}
                      className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Voir</span>
                    </button>
                    <button 
                      onClick={() => handleStartEdit(post)}
                      className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Modifier</span>
                    </button>
                    <button 
                      onClick={() => handleExport(post)}
                      className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Exporter</span>
                    </button>
                    <button 
                      onClick={() => handleDelete(post.id)}
                      className="flex items-center space-x-1 text-gray-600 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucune offre trouvée</h3>
          <p className="text-gray-500">
            {searchTerm 
              ? "Aucune offre ne correspond à votre recherche" 
              : "Vous n'avez pas encore sauvegardé d'offres d'emploi"}
          </p>
          {!searchTerm && (
            <button 
              onClick={() => window.location.href = '/générer'}
              className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
            >
              Générer une offre
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedPosts;